import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { PageHeader, Panel, StatusPill, Empty, Skeleton, Icon, useToast } from '../../components/ui';
import { previewOutreachImport, commitOutreachImport, listOutreachImports } from '../../utils/api';
import { fmtDateTime, loadError } from '../utils';

const STEPS = ['Upload workbook', 'Review rows', 'Import'];
const MAX_ROWS = 2000;
const MAX_BYTES = 8 * 1024 * 1024;
const ACTION_PILL = { create: 'new', update: 'contacted', skip: 'skipped', error: 'failed' };
const ACTION_LABEL = { create: 'Add', update: 'Update', skip: 'Skip', error: 'Error' };

// Date cells arrive as Date objects when the sheet is read with cellDates. Send them as
// YYYY-MM-DD so the server does not have to guess the browser's timezone.
function cellValue(v) {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? '' : v.toISOString().slice(0, 10);
  if (v == null) return '';
  return typeof v === 'string' ? v.trim() : v;
}

export default function ImportPage() {
  const [showToast, toastNode] = useToast();
  const [step, setStep] = useState(1);
  const [over, setOver] = useState(false);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState(null);
  const [filter, setFilter] = useState('');

  const loadHistory = useCallback(() => listOutreachImports({ limit: 25 }).then(r => setHistory(r.items || [])).catch(() => setHistory([])), []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const reset = () => { setStep(1); setFile(null); setRows(null); setPreview(null); setResult(null); setError(''); setFilter(''); };

  const readFile = async (f) => {
    setError('');
    if (!f) return;
    if (!/\.(xlsx|xlsm|xls|csv)$/i.test(f.name)) { setError('Choose an Excel workbook (.xlsx) or a CSV file.'); return; }
    if (f.size > MAX_BYTES) { setError('That file is larger than 8 MB. Export only the outreach sheet and try again.'); return; }
    setBusy(true);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' });
      const cleaned = raw.map(r => r.map(cellValue)).filter(r => r.some(c => c !== ''));
      if (cleaned.length < 2) { setError('The first sheet has no data rows under the header.'); return; }
      if (cleaned.length - 1 > MAX_ROWS) { setError(`The sheet has ${cleaned.length - 1} rows. The limit is ${MAX_ROWS} per import.`); return; }
      const p = await previewOutreachImport({ rows: cleaned, fileName: f.name });
      setFile(f); setRows(cleaned); setPreview(p); setStep(2);
    } catch (e) { setError(loadError(e, 'Could not read this workbook')); }
    finally { setBusy(false); }
  };

  const onDrop = (e) => { e.preventDefault(); setOver(false); readFile(e.dataTransfer.files?.[0]); };

  const commit = async () => {
    setBusy(true); setError('');
    try {
      const r = await commitOutreachImport({ rows, fileName: file.name });
      setResult(r);
      setStep(3);
      showToast(r.alreadyImported ? 'This workbook was already imported' : 'Import complete');
      loadHistory();
    } catch (e) { setError(loadError(e, 'Import failed')); }
    finally { setBusy(false); }
  };

  const items = preview?.items || [];
  const shown = filter ? items.filter(i => i.action === filter) : items;
  const counts = preview?.counts || {};

  return (
    <>
      <PageHeader
        title="Import outreach workbook"
        subtitle="Load the Pontiac Medicaid provider outreach spreadsheet. Rows are matched to existing offices by name and address; staff fields are updated and agent-verified facts are left alone."
        actions={step > 1 ? <button type="button" className="ops-btn ops-btn--ghost ops-btn--sm" onClick={reset}>Start over</button> : null}
      />

      <Panel>
        <ol className="ops-stepper" style={{ marginBottom: 18 }}>
          {STEPS.map((l, i) => (
            <React.Fragment key={l}>
              {i > 0 && <li className="ops-step-line" aria-hidden="true" />}
              <li className={`ops-step${step === i + 1 ? ' is-active' : step > i + 1 ? ' is-done' : ''}`}><span>{step > i + 1 ? <Icon name="check" size={14} /> : i + 1}</span>{l}</li>
            </React.Fragment>
          ))}
        </ol>
        {error && <div className="ops-error" style={{ marginBottom: 14 }}>{error}</div>}

        {step === 1 && (
          <>
            <div className={`ops-dropzone${over ? ' is-over' : ''}`} onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop}>
              <strong>{busy ? 'Reading workbook' : 'Drop the workbook here or choose a file'}</strong>
              <span className="ops-help">Expected columns: Lead ID, Priority, Office/Practice, Practice Type, Street Address, City, ZIP Code, Phone, Medicaid Participation Signal, Contact Verification, Why Contact, Source URL, Reviewed On, Outreach Status, Assigned Staff, Last Contacted, Next Follow-up, Contact Person, Contact Outcome/Notes, Data Flags/Caveats. Only the office name is required.</span>
              <div style={{ marginTop: 12 }}>
                <label className="ops-btn ops-btn--primary ops-btn--sm" style={{ cursor: 'pointer' }}>
                  <Icon name="upload" size={14} /> Choose file
                  <input type="file" accept=".xlsx,.xlsm,.xls,.csv" style={{ display: 'none' }} disabled={busy} onChange={e => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
                </label>
              </div>
            </div>
            <div className="ops-help" style={{ marginTop: 10 }}>The file is parsed in your browser and only the cell values are sent to the dashboard API. Importing the same file twice is safe: it is recognised by content and skipped.</div>
          </>
        )}

        {step === 2 && preview && (
          <>
            {preview.alreadyImported && <div className="ops-note ops-note--review" style={{ marginBottom: 12 }}>This exact workbook has already been imported. Importing again makes no changes.</div>}
            <div className="ops-stat-row" style={{ marginBottom: 12 }}>
              <div className="ops-stat"><b>{preview.rowCount}</b><span>Rows in {file?.name}</span></div>
              <div className="ops-stat"><b>{counts.create ?? 0}</b><span>New offices</span></div>
              <div className="ops-stat"><b>{counts.update ?? 0}</b><span>Updates to existing</span></div>
              <div className="ops-stat"><b>{counts.skip ?? 0}</b><span>Skipped</span></div>
              <div className="ops-stat"><b>{counts.error ?? 0}</b><span>Errors</span></div>
              {counts.duplicateRows > 0 && <div className="ops-stat"><b>{counts.duplicateRows}</b><span>Duplicate rows in file</span></div>}
            </div>
            {!!preview.unmapped?.length && <div className="ops-help" style={{ marginBottom: 10 }}>Columns ignored: {preview.unmapped.join(', ')}</div>}
            <div className="ops-filters" style={{ marginBottom: 10 }}>
              {['', 'create', 'update', 'skip', 'error'].map(k => <button key={k || 'all'} type="button" className={`ops-btn ops-btn--sm ${filter === k ? 'ops-btn--navy' : 'ops-btn--ghost'}`} onClick={() => setFilter(k)}>{k ? ACTION_LABEL[k] : 'All'}</button>)}
            </div>
            <div className="ops-table-wrap" style={{ maxHeight: 420, overflow: 'auto' }}>
              <table className="ops-table ops-table--dense">
                <thead><tr><th className="num">Row</th><th>Office</th><th>Address</th><th>Phone</th><th>Action</th><th>Notes</th></tr></thead>
                <tbody>
                  {shown.map(i => (
                    <tr key={i.rowNumber}>
                      <td className="num">{i.rowNumber}</td>
                      <td><div className="ops-cell-main">{i.name || <span className="muted">Missing name</span>}</div></td>
                      <td className="ops-cell-sub">{[i.street, i.city, i.zip].filter(Boolean).join(', ')}</td>
                      <td className="mono">{i.phone || ''}</td>
                      <td><StatusPill status={ACTION_PILL[i.action] || 'neutral'} label={ACTION_LABEL[i.action] || i.action} /></td>
                      <td className="ops-cell-sub">{[...(i.problems || []), ...(i.warnings || [])].join('; ')}</td>
                    </tr>
                  ))}
                  {!shown.length && <tr><td colSpan={6}><Empty title="No rows in this group" /></td></tr>}
                </tbody>
              </table>
            </div>
            <div className="ops-actions" style={{ justifyContent: 'flex-end', marginTop: 14 }}>
              <button type="button" className="ops-btn ops-btn--ghost" onClick={reset} disabled={busy}>Cancel</button>
              <button type="button" className="ops-btn ops-btn--primary" onClick={commit} disabled={busy || preview.alreadyImported || (!counts.create && !counts.update)}>{busy ? 'Importing' : `Import ${(counts.create || 0) + (counts.update || 0)} rows`}</button>
            </div>
          </>
        )}

        {step === 3 && result && (
          <>
            <div className="ops-stat-row" style={{ marginBottom: 12 }}>
              <div className="ops-stat"><b>{result.batch?.results?.created ?? 0}</b><span>Offices added</span></div>
              <div className="ops-stat"><b>{result.batch?.results?.updated ?? 0}</b><span>Offices updated</span></div>
              <div className="ops-stat"><b>{result.batch?.results?.skipped ?? 0}</b><span>Skipped</span></div>
              <div className="ops-stat"><b>{result.batch?.results?.errors ?? 0}</b><span>Errors</span></div>
              <div className="ops-stat"><b>{result.batch?.results?.conflicts ?? 0}</b><span>Conflicts</span></div>
            </div>
            {result.alreadyImported ? <div className="ops-note">This workbook had already been imported on {fmtDateTime(result.batch?.createdAt)}. Nothing changed.</div> : <div className="ops-note">Imported offices are marked as unverified until the next research run checks them against public sources.</div>}
            <div className="ops-actions" style={{ marginTop: 14 }}>
              <Link className="ops-btn ops-btn--primary" to="/outreach/offices?origin=import">Open imported offices</Link>
              <button type="button" className="ops-btn ops-btn--ghost" onClick={reset}>Import another file</button>
            </div>
          </>
        )}
      </Panel>

      <div style={{ marginTop: 18 }}>
        <Panel title="Import history" flush>
          {!history ? <div style={{ padding: 18 }}><Skeleton rows={3} /></div> : !history.length ? <Empty title="No workbooks imported yet" /> : (
            <div className="ops-table-wrap">
              <table className="ops-table ops-table--dense">
                <thead><tr><th>Imported</th><th>File</th><th>By</th><th className="num">Rows</th><th className="num">Added</th><th className="num">Updated</th><th className="num">Skipped</th><th className="num">Errors</th></tr></thead>
                <tbody>
                  {history.map(b => (
                    <tr key={b.batchId}>
                      <td>{fmtDateTime(b.createdAt)}</td>
                      <td className="ops-cell-main">{b.fileName}</td>
                      <td>{b.createdBy || ''}</td>
                      <td className="num">{b.rowCount ?? ''}</td>
                      <td className="num">{b.results?.created ?? 0}</td>
                      <td className="num">{b.results?.updated ?? 0}</td>
                      <td className="num">{b.results?.skipped ?? 0}</td>
                      <td className="num">{(b.results?.errors ?? 0) + (b.results?.conflicts ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
      {toastNode}
    </>
  );
}
